// Compliance-certificate recipients for one customer (used inside the customer edit form).
// Part of the Reference page (components/Reference.jsx). Split out 2026-10-14.
import { useState, useEffect } from 'react';
import { postJson, fetchJson } from '../../api.js';

// CertRecipientsPanel — CRUD list of compliance-certificate recipients
// for one customer. SEPARATE from statement contacts: certificates go
// to a different group of people (ops / EHS). Backed by
// /api/customer-certificate-recipients.
export default function CertRecipientsPanel({ customerName }) {
  // Data: this customer's certificate recipients (loaded from /api/compliance-certificates/recipients).
  const [rows, setRows] = useState(null);
  const [editRow, setEditRow] = useState(null);   // { id?, email, name, is_primary }
  const [busy, setBusy] = useState(false);
  // Actions: load, save one row (add or edit), delete.
  async function load() {
    try {
      const r = await fetchJson('/api/customer-certificate-recipients?customer=' + encodeURIComponent(customerName));
      setRows(r.recipients || []);
    } catch (e) { setRows([]); }
  }
  useEffect(() => { if (customerName) load(); }, [customerName]);
  async function saveRow() {
    if (!editRow || !editRow.email || !/@/.test(editRow.email)) { alert('Valid email required.'); return; }
    setBusy(true);
    try {
      if (editRow.id) {
        await fetch('/api/customer-certificate-recipients/' + editRow.id, {
          method: 'PUT', headers: { 'Content-Type': 'application/json' }, credentials: 'include',
          body: JSON.stringify({ email: editRow.email, name: editRow.name || null, is_primary: !!editRow.is_primary })
        });
      } else {
        await postJson('/api/customer-certificate-recipients', {
          customer: customerName, email: editRow.email, name: editRow.name || null, is_primary: !!editRow.is_primary
        });
      }
      setEditRow(null); await load();
    } catch (e) { alert('Save failed: ' + e.message); }
    finally { setBusy(false); }
  }
  async function delRow(id) {
    if (!confirm('Remove this certificate recipient?')) return;
    setBusy(true);
    try {
      await fetch('/api/customer-certificate-recipients/' + id, { method: 'DELETE', credentials: 'include' });
      await load();
    } catch (e) { alert('Delete failed: ' + e.message); }
    finally { setBusy(false); }
  }
  const list = rows || [];
  // Screen: header with + Add, then an inline-editable table.
  return (
    <div style={{marginTop:'0.6rem', borderTop:'1px solid #e5e7eb', paddingTop:'0.4rem'}}>
      <div style={{display:'flex', alignItems:'baseline', gap:'0.5rem'}}>
        <strong>🛡 Certificate recipients</strong>
        <span className="small" style={{color:'#666'}}>{list.length === 0 ? 'none — certificates cannot be emailed until one is added' : list.length + ' recipient' + (list.length === 1 ? '' : 's')}</span>
        <button className="refresh-btn small" style={{marginLeft:'auto', background:'#0f766e'}}
                onClick={() => setEditRow({ email:'', name:'', is_primary: list.length === 0 })}
                disabled={!!editRow}>+ Add recipient</button>
      </div>
      {(list.length > 0 || editRow) && (
        <div style={{maxHeight:'10em', overflowY:'auto', marginTop:'0.3rem', fontSize:'0.85em'}}>
          <table className="datatable" style={{margin:0}}>
            <thead><tr><th>Email</th><th>Name</th><th className="num">Primary</th><th style={{width:'8em'}}></th></tr></thead>
            <tbody>
              {editRow && !editRow.id && (
                <tr style={{background:'#f0fdfa'}}>
                  <td><input value={editRow.email} onChange={e=>setEditRow({...editRow, email:e.target.value})} placeholder="name@example.com" style={{width:'100%'}} /></td>
                  <td><input value={editRow.name||''} onChange={e=>setEditRow({...editRow, name:e.target.value})} style={{width:'100%'}} /></td>
                  <td className="num"><input type="checkbox" checked={!!editRow.is_primary} onChange={e=>setEditRow({...editRow, is_primary:e.target.checked})} /></td>
                  <td>
                    <button className="refresh-btn small" onClick={saveRow} disabled={busy}>Save</button>
                    <button className="refresh-btn small" style={{background:'#94a3b8', marginLeft:4}} onClick={() => setEditRow(null)} disabled={busy}>Cancel</button>
                  </td>
                </tr>
              )}
              {list.map(c => editRow && editRow.id === c.id ? (
                <tr key={c.id} style={{background:'#fef3c7'}}>
                  <td><input value={editRow.email} onChange={e=>setEditRow({...editRow, email:e.target.value})} style={{width:'100%'}} /></td>
                  <td><input value={editRow.name||''} onChange={e=>setEditRow({...editRow, name:e.target.value})} style={{width:'100%'}} /></td>
                  <td className="num"><input type="checkbox" checked={!!editRow.is_primary} onChange={e=>setEditRow({...editRow, is_primary:e.target.checked})} /></td>
                  <td>
                    <button className="refresh-btn small" onClick={saveRow} disabled={busy}>Save</button>
                    <button className="refresh-btn small" style={{background:'#94a3b8', marginLeft:4}} onClick={() => setEditRow(null)} disabled={busy}>Cancel</button>
                  </td>
                </tr>
              ) : (
                <tr key={c.id}>
                  <td><code>{c.email}</code>{c.is_primary && <span className="small" style={{marginLeft:4, color:'#0f766e', fontWeight:600}}>★</span>}</td>
                  <td className="small">{c.name || ''}</td>
                  <td className="num">{c.is_primary ? '✓' : ''}</td>
                  <td>
                    <button className="refresh-btn small" onClick={() => setEditRow({ ...c })} disabled={!!editRow || busy}>Edit</button>
                    <button className="refresh-btn small" style={{background:'#b91c1c', marginLeft:4}} onClick={() => delRow(c.id)} disabled={!!editRow || busy}>×</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
